// The component header's search box: address/city/postcode autocomplete,
// "Use my location", and a radius select. All state here is IN-MEMORY ONLY
// (GDPR: no storage) and reset on every reconnect, same as the rest of the
// component.
//
// SEARCH DRIVES THE MAP, NEVER THE REVERSE (F-039). A search renders results to the list, then
// fits the camera to them (StoradarMap.fitCameraToResults) — the only camera-moving path in this
// component. Panning or zooming the map afterwards does not re-run the query; there is no
// "search as I move the map" mode.
//
// THE RADIUS IS A RESULTS FILTER, NOT A CAMERA CONTROL (F-039). Picking "50 km" asks "show me
// what's within 50 km", and the map then frames whatever that turned out to be. It never fits the
// camera to the radius circle — a 50 km circle around a town with three shops in one corner would
// otherwise leave the visitor staring mostly at fields. This module therefore only reports the
// chosen radius to the component; every camera decision lives in StoradarMap.fitCameraToResults.
//
// A11Y: the input follows the ARIA 1.2 combobox-with-listbox pattern —
// role="combobox" on the input, aria-expanded/aria-controls/aria-activedescendant
// wired to a role="listbox" popup, Arrow keys move the active descendant,
// Enter selects, Escape closes without clearing the typed text.
//
// GEOLOCATION (GDPR): navigator.geolocation is called ONLY from the button's
// click handler — never on boot, never automatically. A denial shows one quiet
// inline message and the component never asks again on its own; a further click
// is a fresh explicit visitor action, not a re-prompt we initiate.

import type { DataSource } from "../data/DataSource";
import type { AutocompleteSuggestion, SearchCenter } from "../data/types";
import { debounce, type Debounced } from "../util/debounce";
import type { ResolvedUnit } from "../util/units";

const AUTOCOMPLETE_DEBOUNCE_MS = 300;
/** Radius choices in the LOCATOR's unit system (F-032: "10/25/50/100 km|mi"). */
const RADIUS_STEPS = [10, 25, 50, 100];
const METERS_PER_MILE = 1609.344;

export interface SearchBarHandlers {
  /**
   * A concrete search center was chosen (autocomplete pick or "use my location"). [radiusMeters]
   * bounds the QUERY; where the map ends up is decided by what the query returns (F-039).
   */
  onSearchCenter(center: SearchCenter, radiusMeters: number): void;
  /** The radius changed while a search center is already active; re-run the query with it. */
  onRadiusChange(radiusMeters: number): void;
  /** The visitor cleared the search box back to the unfiltered map. */
  onClearSearch(): void;
}

export class SearchBar {
  readonly element: HTMLElement;

  private readonly input: HTMLInputElement;
  private readonly listbox: HTMLUListElement;
  private readonly statusEl: HTMLElement;
  private readonly geoButton: HTMLButtonElement;
  private readonly radiusSelect: HTMLSelectElement;

  private suggestions: AutocompleteSuggestion[] = [];
  private activeIndex = -1;
  private readonly debouncedSearch: Debounced<[string]>;
  private inFlight: AbortController | null = null;
  private static idCounter = 0;
  private readonly instanceId = `sd-search-${++SearchBar.idCounter}`;

  constructor(
    private readonly dataSource: DataSource,
    private unit: ResolvedUnit,
    private readonly handlers: SearchBarHandlers,
  ) {
    this.element = div("search-bar");

    const row = div("search-row");
    const comboWrap = div("search-combo");

    this.input = document.createElement("input");
    this.input.type = "search";
    this.input.className = "search-input";
    this.input.placeholder = "Search address, city, or postcode";
    this.input.setAttribute("role", "combobox");
    this.input.setAttribute("aria-expanded", "false");
    this.input.setAttribute("aria-autocomplete", "list");
    this.input.setAttribute("aria-controls", `${this.instanceId}-listbox`);
    this.input.setAttribute("autocomplete", "off");
    this.input.addEventListener("input", () => this.onInput());
    this.input.addEventListener("keydown", (e) => this.onKeyDown(e));
    this.input.addEventListener("blur", () => {
      // Defer so a click on a suggestion (which also blurs the input) still registers.
      setTimeout(() => this.closeDropdown(), 150);
    });

    this.listbox = document.createElement("ul");
    this.listbox.id = `${this.instanceId}-listbox`;
    this.listbox.className = "search-listbox";
    this.listbox.setAttribute("role", "listbox");
    this.listbox.hidden = true;

    this.statusEl = tag("p", "search-status", "");
    this.statusEl.setAttribute("role", "status");
    this.statusEl.hidden = true;

    comboWrap.append(this.input, this.listbox);

    this.geoButton = document.createElement("button");
    this.geoButton.type = "button";
    this.geoButton.className = "search-geo-button";
    this.geoButton.textContent = "Use my location";
    this.geoButton.setAttribute("aria-label", "Use my location");
    this.geoButton.addEventListener("click", () => this.onUseMyLocation());

    row.append(comboWrap, this.geoButton);

    const controls = div("search-controls");
    const radiusLabel = document.createElement("label");
    radiusLabel.className = "search-radius-label";
    radiusLabel.textContent = "Radius";
    this.radiusSelect = document.createElement("select");
    this.radiusSelect.className = "search-radius";
    this.populateRadiusOptions();
    this.radiusSelect.addEventListener("change", () => this.handlers.onRadiusChange(this.currentRadiusMeters()));
    radiusLabel.appendChild(this.radiusSelect);

    controls.append(radiusLabel);

    this.element.append(row, this.statusEl, controls);

    this.debouncedSearch = debounce((query: string) => void this.runSearch(query), AUTOCOMPLETE_DEBOUNCE_MS);
  }

  /** Updates the display unit (locator config resolves AUTO after construction in rare races). */
  setUnit(unit: ResolvedUnit): void {
    if (unit === this.unit) return;
    this.unit = unit;
    this.populateRadiusOptions();
  }

  /** The currently selected radius, in meters. */
  currentRadiusMeters(): number {
    const km = Number(this.radiusSelect.value);
    return this.unit === "MI" ? km * METERS_PER_MILE : km * 1000;
  }

  /** Human label for the current radius, in the display unit (for empty-state copy). */
  currentRadiusLabel(): string {
    return formatRadiusOption(Number(this.radiusSelect.value), this.unit);
  }

  /** Widens the radius one step (empty-state "widen radius" affordance) and reports the new value. */
  widenRadius(): number | null {
    const options = Array.from(this.radiusSelect.options);
    const index = options.findIndex((o) => o.selected);
    if (index < 0 || index >= options.length - 1) {
      return null; // already at the widest step
    }
    this.radiusSelect.selectedIndex = index + 1;
    return this.currentRadiusMeters();
  }

  private populateRadiusOptions(): void {
    const selectedKm = this.radiusSelect.value ? Number(this.radiusSelect.value) : RADIUS_STEPS[1];
    this.radiusSelect.replaceChildren();
    for (const step of RADIUS_STEPS) {
      const option = document.createElement("option");
      option.value = String(step);
      option.textContent = formatRadiusOption(step, this.unit);
      this.radiusSelect.appendChild(option);
    }
    this.radiusSelect.value = String(RADIUS_STEPS.includes(selectedKm) ? selectedKm : RADIUS_STEPS[1]);
  }

  private onInput(): void {
    const query = this.input.value;
    if (query.trim().length === 0) {
      this.debouncedSearch.cancel();
      this.closeDropdown();
      this.handlers.onClearSearch();
      return;
    }
    this.debouncedSearch(query);
  }

  private async runSearch(query: string): Promise<void> {
    this.inFlight?.abort();
    const controller = new AbortController();
    this.inFlight = controller;
    try {
      const payload = await this.dataSource.loadAutocomplete(query, controller.signal);
      if (controller.signal.aborted) return;
      if (!payload.available) {
        this.suggestions = [];
        this.renderSuggestions();
        this.showStatus("Search is temporarily unavailable. You can still browse the map.");
        return;
      }
      this.suggestions = payload.suggestions;
      this.hideStatus();
      this.renderSuggestions();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      this.suggestions = [];
      this.renderSuggestions();
      this.showStatus("Search is temporarily unavailable. You can still browse the map.");
    }
  }

  private renderSuggestions(): void {
    this.listbox.replaceChildren();
    this.activeIndex = -1;
    this.input.removeAttribute("aria-activedescendant");

    if (this.suggestions.length === 0) {
      this.closeDropdown();
      return;
    }
    this.suggestions.forEach((suggestion, index) => {
      const li = document.createElement("li");
      li.id = `${this.instanceId}-option-${index}`;
      li.className = "search-option";
      li.setAttribute("role", "option");
      li.textContent = suggestion.label;
      li.addEventListener("mousedown", (e) => {
        // mousedown (not click) fires before the input's blur, so selection wins the race.
        e.preventDefault();
        this.selectSuggestion(index);
      });
      this.listbox.appendChild(li);
    });
    this.listbox.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  }

  private selectSuggestion(index: number): void {
    const suggestion = this.suggestions[index];
    if (!suggestion) return;
    this.input.value = suggestion.label;
    this.closeDropdown();
    this.handlers.onSearchCenter({ lat: suggestion.lat, lng: suggestion.lng }, this.currentRadiusMeters());
  }

  private closeDropdown(): void {
    this.listbox.hidden = true;
    this.listbox.replaceChildren();
    this.activeIndex = -1;
    this.input.setAttribute("aria-expanded", "false");
    this.input.removeAttribute("aria-activedescendant");
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (this.listbox.hidden || this.suggestions.length === 0) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveActive(-1);
        break;
      case "Enter":
        // Typing a place and pressing Enter is a search, not a no-op: with no option explicitly
        // arrowed to, the top suggestion is what the visitor means (ARIA 1.2 lets a combobox
        // commit its first option). Without this, "type a query" was a dead entry path.
        event.preventDefault();
        this.selectSuggestion(this.activeIndex >= 0 ? this.activeIndex : 0);
        break;
      case "Escape":
        event.preventDefault();
        this.closeDropdown();
        break;
      default:
        break;
    }
  }

  private moveActive(delta: number): void {
    const count = this.suggestions.length;
    this.activeIndex = ((this.activeIndex + delta) % count + count) % count;
    const options = this.listbox.querySelectorAll<HTMLLIElement>(".search-option");
    options.forEach((el, i) => el.classList.toggle("is-active", i === this.activeIndex));
    const activeOption = options[this.activeIndex];
    if (activeOption) {
      this.input.setAttribute("aria-activedescendant", activeOption.id);
      activeOption.scrollIntoView({ block: "nearest" });
    }
  }

  /**
   * Geolocation ONLY on this explicit tap (GDPR). A denial shows one quiet
   * inline message; we never re-prompt on our own — a subsequent tap is the
   * visitor's own fresh action, not something this component initiates.
   */
  private onUseMyLocation(): void {
    if (!("geolocation" in navigator)) {
      this.showStatus("Location isn't available in this browser.");
      return;
    }
    this.geoButton.disabled = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.geoButton.disabled = false;
        this.hideStatus();
        this.handlers.onSearchCenter(
          { lat: position.coords.latitude, lng: position.coords.longitude },
          this.currentRadiusMeters(),
        );
      },
      () => {
        this.geoButton.disabled = false;
        // Quiet, once — no modal, no repeated prompt, no nagging.
        this.showStatus("Location access wasn't available. You can still search by address.");
      },
      { maximumAge: 60_000, timeout: 10_000 },
    );
  }

  private showStatus(message: string): void {
    this.statusEl.textContent = message;
    this.statusEl.hidden = false;
  }

  private hideStatus(): void {
    this.statusEl.hidden = true;
    this.statusEl.textContent = "";
  }
}

/** Renders a radius option label honoring the resolved unit (F-037). */
function formatRadiusOption(step: number, unit: ResolvedUnit): string {
  return unit === "MI" ? `${step} mi` : `${step} km`;
}

function div(className: string): HTMLElement {
  const node = document.createElement("div");
  node.className = className;
  return node;
}

function tag(name: string, className: string, content: string): HTMLElement {
  const node = document.createElement(name);
  node.className = className;
  node.textContent = content;
  return node;
}
